export type SentryEnvironmentConfig = {
  environment: string;
  enabled: boolean;
  release: string;
  sampleRate: number;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  diyEventIds?: string[];
  heroesEventIds?: string[];
  ironmanEventIds?: string[];
  rnrEventIds?: string[];
  sjmmwEventIds?: string[];
  memorialsIds?: string[];
};

export const getSentryConfig = ({
  hostname = '',
}: Location): SentryEnvironmentConfig => {
  switch (hostname) {
    case 'secure2.convio.net':
      return {
        environment: 'dev',
        enabled: false,
        release: process.env.REACT_APP_SENTRY_VERSION || '',
        sampleRate: 0,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
      };
    case 'fundraising.qa.stjude.org':
      return {
        environment: 'qa',
        enabled: true,
        release: process.env.REACT_APP_SENTRY_VERSION || '',
        sampleRate: 1.0,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.1,
        diyEventIds: [
          '26582',
          '26585',
          '26588',
          '26704',
          '26709',
          '26720',
          '26721',
          '26726',
          '26727',
          '26728',
          '26729',
          '26730',
          '26732',
          '28303',
          '28333',
          '28344',
          '28345',
          '28346',
          '28347',
          '28463',
          '28464',
          '28465',
          '28466',
          '28467',
          '28468',
          '28503',
          '28513',
          '28523',
          '28524',
          '28525',
          '28526',
          '28533',
          '28563',
          '28564',
          '28583',
          '28593',
          '28623',
          '28624',
          '28625',
          '28626',
          '28627',
          '28628',
          '28629',
          '28703',
          '28713',
          '28714',
        ],
        heroesEventIds: [
          '27075',
          '27077',
          '27079',
          '27593',
          '27776',
          '27924',
          '28003',
          '28103',
          '28123',
          '28364',
          '28493',
          '135394',
          '135689',
          '135980',
          '137855',
          '137858',
          '141067',
          '141667',
          '141669',
          '141910',
          '142107',
          '142507',
          '143178',
          '143314',
          '143315',
          '143504',
          '145798',
          '146557',
          '150717',
          '151190',
        ],
        ironmanEventIds: ['26880', '27866', '28114'],
        rnrEventIds: [
          '26776',
          '26781',
          '27024',
          '27083',
          '27913',
          '27915',
          '27933',
          '27934',
          '28165',
          '28225',
          '28293',
          '28544',
        ],
        sjmmwEventIds: ['26663', '27074', '28198', '30123', '31117'],
        memorialsIds: ['26725', '27023'],
      };
    case 'fundraising.stjude.org':
      return {
        environment: 'prod',
        enabled: true,
        release: process.env.REACT_APP_SENTRY_VERSION || '',
        sampleRate: 1.0,
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0.5,
        diyEventIds: [
          '22340',
          '44326',
          '59427',
          '86793',
          '87944',
          '122933',
          '122934',
          '122935',
          '132623',
          '132689',
          '132691',
          '132692',
          '133377',
          '133385',
          '133397',
          '133398',
          '133399',
          '134030',
          '134326',
          '134327',
          '134329',
          '134330',
          '134331',
          '146927',
          '146928',
          '146929',
          '146930',
          '146931',
          '146932',
          '146933',
          '146934',
          '146935',
          '150357',
          '150358',
          '150359',
          '150360',
          '150361',
          '150362',
          '150363',
          '150364',
          '150365',
          '150366',
          '150367',
          '150368',
          '150369',
          '150370',
          '150371',
          '150372',
          '150373',
          '150374',
          '150375',
          '150376',
          '151191',
          '151192',
          '151193',
        ],
        heroesEventIds: [
          '27075',
          '27077',
          '27079',
          '27593',
          '27776',
          '27924',
          '28003',
          '28103',
          '28123',
          '28364',
          '28493',
          '135394',
          '135689',
          '135980',
          '137855',
          '137858',
          '141067',
          '141667',
          '141669',
          '141910',
          '142107',
          '142507',
          '143178',
          '143314',
          '143315',
          '143504',
          '145798',
          '146557',
          '150717',
          '151190',
        ],
        ironmanEventIds: ['135233', '141975'],
        rnrEventIds: [
          '137263',
          '137286',
          '137510',
          '138457',
          '139269',
          '141063',
          '142184',
          '142185',
          '142186',
          '142187',
          '143510',
          '144069',
          '145338',
          '145813',
          '146293',
        ],
        sjmmwEventIds: ['131089', '137507', '144988', '154861', '158671'],
      };
    default:
      return {
        environment: 'local',
        enabled: false,
        release: process.env.REACT_APP_SENTRY_VERSION || '',
        sampleRate: 0,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        diyEventIds: [
          '26582',
          '26585',
          '26588',
          '26704',
          '26709',
          '26720',
          '26721',
          '26726',
          '26727',
          '26728',
          '26729',
          '26730',
          '26732',
          '28303',
          '28333',
          '28344',
          '28345',
          '28346',
          '28347',
          '28463',
          '28464',
          '28465',
          '28466',
          '28467',
          '28468',
          '28503',
          '28513',
          '28523',
          '28524',
          '28525',
          '28526',
          '28533',
          '28563',
          '28564',
          '28583',
          '28593',
          '28623',
          '28624',
          '28625',
          '28626',
          '28627',
          '28628',
          '28629',
          '28703',
          '28713',
          '28714',
        ],
        heroesEventIds: [
          '27075',
          '27077',
          '27079',
          '27593',
          '27776',
          '27924',
          '28003',
          '28103',
          '28123',
          '28364',
          '28493',
          '135394',
          '135689',
          '135980',
          '137855',
          '137858',
          '141067',
          '141667',
          '141669',
          '141910',
          '142107',
          '142507',
          '143178',
          '143314',
          '143315',
          '143504',
          '145798',
          '146557',
          '150717',
          '151190',
        ],
        ironmanEventIds: ['26880', '27866', '28114'],
        rnrEventIds: [
          '26776',
          '26781',
          '27024',
          '27083',
          '27913',
          '27915',
          '27933',
          '27934',
          '28165',
          '28225',
          '28293',
          '28544',
        ],
        sjmmwEventIds: ['26663', '27074', '28198'],
        memorialsIds: ['26725', '27023'],
      };
  }
};
